import {Drawer, IDrawerProps} from "@montana/shared/drawer";
import {IPromotionalMenuCard as ILocalPromotionalMenuCard} from "./drawer-menu.types";
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {NavItem} from "@montana/shared/navigation/nav-item";
import "./drawer-menu.styles.scss";
import {useMediaQuery} from "@montana/hooks/useMediaQuery";
import {PromotionalMenuCard} from "@montana/shared/promotional-menu-card";
import {ILevel1MenuItem, ILevel2MenuItem, ILevel3MenuItem, IMainMenu, IPromotionalMenuCard} from "@montana/models/generated/cms.interface";
import {useTranslations} from "@montana/containers/translations";

export type TMenuItem = ILevel1MenuItem | ILevel2MenuItem | ILevel3MenuItem;

export type DrawerMenuProps = {
    menuContent: Omit<IMainMenu, 'footer'>;
    selectedItemId?: string;
} & Omit<IDrawerProps, 'children'>;

export interface INavigationLevel {
    title: string;
    items: (ILevel1MenuItem | ILevel2MenuItem | ILevel3MenuItem)[];
    level: number;
    parent?: ILevel1MenuItem | ILevel2MenuItem;
    triggerId?: string;
    promotionalMenuCards?: IPromotionalMenuCard[];
}

export function DrawerMenu(props: DrawerMenuProps): JSX.Element {
    const {menuContent, selectedItemId, isOpen, isInstant, onOpenChange, ...drawerProps} = props;
    const {mainMenuItems = [], mainMenuFooterLinks = []} = menuContent;

    const isDesktop = useMediaQuery(`(min-width: 1024px)`);

    const [stack, setStack] = useState<INavigationLevel[]>([]);
    const backButtonRef = useRef<HTMLButtonElement>(null);
    const triggerRefs = useRef<Map<string, HTMLButtonElement | HTMLAnchorElement>>(new Map());

    const translate = useTranslations();

    useEffect(() => {
        if (!isOpen) {
            setStack([]);
            return;
        }

        if (selectedItemId) {
            // Find the selected item and build the stack path
            let stackPath: INavigationLevel[] = [];

            // Check level 1
            const level1Item = mainMenuItems.find(item => item.itemId === selectedItemId);
            if (level1Item) {
                stackPath = [
                    {
                        title: level1Item.link?.name || "",
                        items: level1Item.subItems || [],
                        level: 2,
                        parent: level1Item,
                    }
                ];
            } else {
                // Check level 2 and 3
                for (const level1 of mainMenuItems) {
                    if (!level1.subItems) continue;

                    const level2Item = level1.subItems.find(item => item.itemId === selectedItemId);
                    if (level2Item) {
                        const subItems = level2Item.subItems;
                        stackPath = [
                            {
                                title: level1.link?.name || "",
                                items: (level1.subItems || []) as TMenuItem[],
                                level: 2,
                                parent: level1,
                            },
                            {
                                title: level2Item.link?.name || "",
                                items: (subItems || []) as TMenuItem[],
                                level: 3,
                                parent: level2Item,
                                promotionalMenuCards: level2Item.promotionalMenuCards
                            }
                        ];
                        break;
                    }

                    // Check level 3
                    for (const level2 of level1.subItems) {
                        const level3Items = level2.subItems;
                        if (!level3Items) continue;

                        const level3Item = level3Items.find(item => item.itemId === selectedItemId);
                        if (level3Item) {
                            stackPath = [
                                {
                                    title: level1.link?.name || "",
                                    items: (level1.subItems || []) as TMenuItem[],
                                    level: 2,
                                    parent: level1,
                                },
                                {
                                    title: level2.link?.name || "",
                                    items: (level3Items || []) as TMenuItem[],
                                    level: 3,
                                    parent: level2,
                                    promotionalMenuCards: level2.promotionalMenuCards
                                }
                            ];
                            break;
                        }
                    }

                    if (stackPath.length > 0) break;
                }
            }

            if (stackPath.length > 0) {
                setStack(stackPath);
            }
        } else {
            setStack([
                {
                    title: "Menu",
                    items: mainMenuItems,
                    level: 1
                }
            ]);
        }
    }, [isOpen, selectedItemId, mainMenuItems]);

    const currentLevel = useMemo(() => stack[stack.length - 1], [stack]);

    useEffect(() => {
        if (stack.length > 1 && backButtonRef.current) {
            backButtonRef.current.focus();
        }
    }, [stack.length]);

    const handleNavigate = useCallback((item: TMenuItem) => {
        const subItems = "subItems" in item ? item.subItems : undefined;
        if (subItems && subItems.length > 0) {
            setStack(prev => {
                const currentLevel = prev[prev.length - 1];
                return [...prev, {
                    title: item.link?.name || "",
                    items: subItems as TMenuItem[],
                    level: prev.length > 0 ? currentLevel.level + 1 : 1,
                    parent: item as ILevel1MenuItem | ILevel2MenuItem,
                    triggerId: item.itemId,
                    promotionalMenuCards: "promotionalMenuCards" in item ? (item as ILevel2MenuItem).promotionalMenuCards : undefined
                }];
            });
        }
    }, []);

    const handleBack = useCallback(() => {
        const lastLevel = stack[stack.length - 1];
        setStack(prev => prev.slice(0, -1));

        if (lastLevel?.triggerId) {
            setTimeout(() => {
                triggerRefs.current.get(lastLevel.triggerId!)?.focus();
            }, 0);
        }
    }, [stack]);

    const handleTopLevelClick = (item: ILevel1MenuItem, isActive: boolean) => {
        if (isActive) {
            onOpenChange?.(!isOpen);
            return;
        }

        setStack([
            {
                title: item.link?.name || "",
                items: (item.subItems || []) as TMenuItem[],
                level: 2,
                parent: item,
                triggerId: item.itemId,
            }
        ]);
    };

    const renderItem = (item: TMenuItem) => {
        const subItems = "subItems" in item ? (item as ILevel1MenuItem | ILevel2MenuItem).subItems : undefined;
        const hasSubItems = subItems && subItems.length > 0;

        return (
            <li key={item.itemId}>
                <NavItem
                    ref={(el) => {
                        if (el && item.itemId) triggerRefs.current.set(item.itemId, el);
                        else if (item.itemId) triggerRefs.current.delete(item.itemId);
                    }}
                    href={hasSubItems ? undefined : item.link?.url}
                    onPress={hasSubItems ? () => handleNavigate(item) : undefined}
                    textSize="large"
                    aria-expanded={hasSubItems ? stack.some(s => s.triggerId === item.itemId) : undefined}
                    aria-haspopup={hasSubItems ? "menu" : undefined}
                    image={(item as ILevel3MenuItem)?.image}
                >
                    {item.link?.name}
                </NavItem>
            </li>
        );
    };

    const isRoot = isDesktop ? stack.length === 1 : stack.length <= 1;

    return (
        <Drawer isOpen={isOpen} isInstant={isInstant} {...drawerProps} onOpenChange={onOpenChange} className="drawer-menu">
            <nav className="drawer-menu__container" aria-label="Main navigation">
                <div className="drawer-menu__top">
                    <ul className="drawer-menu__top-list">
                        {mainMenuItems.map((item) => (
                            <li key={item.itemId} className="drawer-menu__top-item">
                                <NavItem
                                    ref={(el) => {
                                        if (el && item.itemId) triggerRefs.current.set(item.itemId, el);
                                        else if (item.itemId) triggerRefs.current.delete(item.itemId);
                                    }}
                                    onPress={() => handleTopLevelClick(item, item.itemId === stack[0]?.parent?.itemId)}
                                    href={item.link?.url}
                                    textSize="large"
                                    showBorder={false}
                                    as={item.subItems?.length ? 'button' : 'link'}
                                    flexWidth={true}
                                    textColor="dimmed"
                                    isActive={item.itemId === stack[0]?.parent?.itemId}
                                    aria-expanded={item.itemId === stack[0]?.parent?.itemId}
                                    aria-haspopup={item.subItems?.length ? "menu" : undefined}
                                    verticalPadding="medium"
                                >
                                    {item.link?.name}
                                </NavItem>
                            </li>
                        ))}
                    </ul>
                </div>

                {!isRoot && (
                    <div className="drawer-menu__header">
                        <NavItem
                            ref={backButtonRef}
                            as="button" textSize="small" showBorder={false} iconPosition="left"
                            iconName="arrow_left" onPress={handleBack} verticalPadding="none"
                            aria-label={`Back to ${stack[stack.length - 2]?.title || 'previous level'}`}
                        >
                            {currentLevel?.parent?.link?.name}
                        </NavItem>
                    </div>
                )}

                <div className="drawer-menu__content">
                    {currentLevel?.title && (
                        <h2 className="sr-only">{currentLevel.title}</h2>
                    )}
                    <ul className="drawer-menu__content-list">
                        {currentLevel?.items.map(renderItem)}
                    </ul>

                    {currentLevel?.level === 3 && currentLevel?.parent?.link?.url && (
                        <div className="drawer-menu__view-all">
                            <NavItem href={currentLevel.parent.link.url} verticalPadding="none" textSize="large"
                                     as="link" underlined={true} showBorder={false}>
                                {translate((t) => t.mainMenu.viewAll, "** View all **")}
                            </NavItem>
                        </div>
                    )}
                </div>

                {currentLevel?.level === 3 && currentLevel.promotionalMenuCards && currentLevel.promotionalMenuCards.length > 0 && (
                    <div className="drawer-menu__promoted">
                        {currentLevel.promotionalMenuCards.map((card, idx) => (
                            <PromotionalMenuCard key={idx} card={card as ILocalPromotionalMenuCard} className="drawer-menu__promoted-item" />
                        ))}
                    </div>
                )}

                {currentLevel?.level <= 3 && !currentLevel.promotionalMenuCards && (
                    <div className="drawer-menu__footer">
                    <h2 className="sr-only">Main Menu Footer Links</h2>
                    <ul className="drawer-menu__footer-list">
                        {mainMenuFooterLinks?.map((link, idx) => (
                            <li key={idx}>
                                <NavItem
                                    href={link.url}
                                    showBorder={false}
                                    textSize="medium"
                                    as="link"
                                    verticalPadding="small"
                                >
                                    {link.name}
                                </NavItem>
                            </li>
                        ))}
                    </ul>
                </div>)}
            </nav>
        </Drawer>
    );
}
